/**
 * API client.
 *
 * Handles token storage, automatic refresh on 401, and a single concurrency
 * guard so a burst of parallel requests after a token expires produces one
 * refresh rather than a stampede.
 */

import type {
  AuditEntry,
  ChangeRow,
  CutoffOverview,
  ExportBatch,
  ExportPreflight,
  SageBridgeCompany,
  SageCompany,
  CutoffSetting,
  FeedbackCategory,
  FeedbackItem,
  FeedbackStatus,
  CrewDayMove,
  CrewDayMoveResult,
  CostCodeMaster,
  CostCodeOption,
  CrewDetail,
  CrewMember,
  CrewOption,
  DayLine,
  JobLocationInput,
  JobLocationRow,
  JobLocationSuggestion,
  SiteCheckIn,
  SiteCheckInInput,
  DayUsage,
  ProvisionalPerson,
  ProvisionalPersonInput,
  EarningsCodeAdminRow,
  EarningsCodeOption,
  JobAssignee,
  JobAssignmentCandidate,
  JobClassificationOption,
  UnassignedJob,
  EarningsCodeUpdate,
  EmployeeAdminRow,
  EmployeeUpsert,
  HealthDb,
  JobWageDetail,
  JobWageRateRow,
  JobWageRateUpsert,
  JobWageSummary,
  JobSummary,
  LaborCodeOption,
  LoginRequest,
  PasswordLoginRequest,
  SubmissionAccepted,
  SubmissionDetail,
  SubmissionInput,
  RegisterFilters,
  RegisterResponse,
  SubmissionSummary,
  CrewWeek,
  JobDetail,
  ProblemCategory,
  ProblemDismissal,
  PayPeriod,
  JobFieldSet,
  JobMaintenanceUpdate,
  JobPerDiem,
  OvertimeRuleRow,
  OvertimeRuleUpsert,
  PerDiemDaysAccepted,
  PerDiemDaysInput,
  JobWriteAudit,
  PeopleUpdate,
  PerDiemRateRow,
  PerDiemRateUpsert,
  PerDiemRegister,
  PerDiemRegisterQuery,
  PerDiemRemoved,
  TimeEntryAdd,
  TimeEntryCorrection,
  TokenResponse,
  WageClassificationRow,
  WeekDetail,
  WeekSummary,
  WorkTypeAdminRow,
  WorkTypeOption,
  WorkTypeUpsert,
} from './types'
// Certifications, kept as their own import so this module's main import list
// is untouched by the feature branch.
import type {
  CertAccess,
  CertDayCheck,
  CertCategoryInput,
  CertCategoryRow,
  CertMatrix,
  CertMatrixQuery,
  CertNotifyContact,
  CertNotifyOverview,
  CertNotifySendResponse,
  CertNotifySettings,
  CertRelationInput,
  CertRelationRow,
  CertRequirementInput,
  CertRequirementRow,
  CertTypeInput,
  CertTypeRow,
  CrewCerts,
  EmployeeCertInput,
  EmployeeCerts,
  MyCerts,
} from './types'

const BASE = '/api'

const ACCESS_KEY = 'nwl.access'
const REFRESH_KEY = 'nwl.refresh'
const USER_KEY = 'nwl.user'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface StoredUser {
  employee_id: number
  employee_no: string
  display_name: string
  role: string
}

// --- Token storage ----------------------------------------------------------

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  user: (): StoredUser | null => {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as StoredUser) : null
  },
  set(t: TokenResponse) {
    localStorage.setItem(ACCESS_KEY, t.access_token)
    localStorage.setItem(REFRESH_KEY, t.refresh_token)
    localStorage.setItem(
      USER_KEY,
      JSON.stringify({
        employee_id: t.employee_id,
        employee_no: t.employee_no,
        display_name: t.display_name,
        role: t.role,
      }),
    )
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
  },
}

// --- Refresh coordination ---------------------------------------------------

let refreshInFlight: Promise<boolean> | null = null

async function refreshTokens(): Promise<boolean> {
  // Collapse concurrent refreshes into one.
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const refresh_token = tokens.refresh()
    if (!refresh_token) return false
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token }),
      })
      if (!res.ok) {
        tokens.clear()
        return false
      }
      tokens.set((await res.json()) as TokenResponse)
      return true
    } catch {
      // Network failure is not an auth failure — keep the tokens so an
      // offline device can retry when signal returns.
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

// --- Core request -----------------------------------------------------------

async function request<T>(
  path: string,
  init: RequestInit = {},
  retryOn401 = true,
): Promise<T> {
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  const access = tokens.access()
  if (access) headers.set('Authorization', `Bearer ${access}`)

  const res = await fetch(`${BASE}${path}`, { ...init, headers })

  if (res.status === 401 && retryOn401) {
    if (await refreshTokens()) {
      return request<T>(path, init, false)
    }
  }

  if (!res.ok) {
    let detail: unknown
    let message = `${res.status} ${res.statusText}`
    try {
      const body = await res.json()
      detail = body?.detail ?? body
      if (typeof detail === 'string') message = detail
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message, detail)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// --- Endpoints --------------------------------------------------------------

export const api = {
  health: () => request<{ status: string }>('/health'),
  healthDb: () => request<HealthDb>('/health/db'),

  async login(payload: LoginRequest): Promise<TokenResponse> {
    const res = await fetch(`${BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      let message = 'Sign in failed'
      if (res.status === 423) message = 'Account locked. Try again shortly.'
      else if (res.status === 401) message = 'Invalid employee number or PIN'
      throw new ApiError(res.status, message)
    }
    const data = (await res.json()) as TokenResponse
    tokens.set(data)
    return data
  },

  async loginWithPassword(payload: PasswordLoginRequest): Promise<TokenResponse> {
    const res = await fetch(`${BASE}/auth/login/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      let message = 'Sign in failed'
      if (res.status === 423) message = 'Account locked. Try again shortly.'
      else if (res.status === 401) message = 'Invalid email or password'
      else if (res.status === 400) {
        // Account has been federated — a local password must no longer work.
        message = 'This account signs in with your Microsoft account.'
      }
      throw new ApiError(res.status, message)
    }
    const data = (await res.json()) as TokenResponse
    tokens.set(data)
    return data
  },

  async logout(): Promise<void> {
    const refresh_token = tokens.refresh()
    if (refresh_token) {
      try {
        await request<void>('/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refresh_token }),
        })
      } catch {
        /* revoking is best-effort; local tokens still get cleared */
      }
    }
    tokens.clear()
  },

  jobs: (search?: string) =>
    request<JobSummary[]>(
      `/reference/jobs${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    ),
  jobCostCodes: (jobNo: string) =>
    request<CostCodeOption[]>(
      `/reference/jobs/${encodeURIComponent(jobNo)}/cost-codes`,
    ),
  earningsCodes: (fieldOnly = true) =>
    request<EarningsCodeOption[]>(
      `/reference/earnings-codes?field_only=${fieldOnly}`,
    ),
  workTypes: (fieldOnly = true) =>
    request<WorkTypeOption[]>(`/reference/work-types?field_only=${fieldOnly}`),
  laborCodes: () => request<LaborCodeOption[]>('/reference/labor-codes'),
  crew: () => request<CrewMember[]>('/reference/crew'),
  crewOptions: () => request<CrewOption[]>('/reference/crews'),

  submit: (payload: SubmissionInput) =>
    request<SubmissionAccepted>('/submissions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  submissions: (params?: { status?: string; job_no?: string }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.job_no) q.set('job_no', params.job_no)
    const qs = q.toString()
    return request<SubmissionSummary[]>(`/submissions${qs ? `?${qs}` : ''}`)
  },
  submission: (id: string) => request<SubmissionDetail>(`/submissions/${id}`),
  correctEntry: (submissionId: string, entryId: string, payload: TimeEntryCorrection) =>
    request<SubmissionDetail>(`/submissions/${submissionId}/entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteEntry: (submissionId: string, entryId: string, reason?: string) =>
    request<SubmissionDetail>(
      `/submissions/${submissionId}/entries/${entryId}${
        reason ? `?reason=${encodeURIComponent(reason)}` : ''
      }`,
      { method: 'DELETE' },
    ),
  addEntry: (submissionId: string, payload: TimeEntryAdd) =>
    request<SubmissionDetail>(`/submissions/${submissionId}/entries`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Move a crew day to another job or date. dry_run previews it. */
  moveCrewDay: (submissionId: string, payload: CrewDayMove) =>
    request<CrewDayMoveResult>(`/submissions/${submissionId}/move`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updatePeople: (submissionId: string, payload: PeopleUpdate) =>
    request<SubmissionDetail>(`/submissions/${submissionId}/people`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  problemsSummary: () => request<ProblemCategory[]>('/problems/summary'),
  problem: (problemType: string) =>
    request<ProblemCategory>(`/problems/${encodeURIComponent(problemType)}`),
  problems: (includeDismissed = false) =>
    request<ProblemCategory[]>(`/problems?include_dismissed=${includeDismissed}`),
  dismissProblem: (payload: ProblemDismissal) =>
    request<ProblemCategory>('/problems/dismiss', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  undismissProblem: (problemType: string, entityKey: string) =>
    request<ProblemCategory>(
      `/problems/dismiss/${encodeURIComponent(problemType)}/${encodeURIComponent(entityKey)}`,
      { method: 'DELETE' },
    ),
  officeJobs: (f: { search?: string; issues_only?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (f.search) q.set('search', f.search)
    if (f.issues_only) q.set('issues_only', 'true')
    const qs = q.toString()
    return request<JobDetail[]>(`/jobs${qs ? `?${qs}` : ''}`)
  },
  perDiemLocations: () => request<string[]>('/jobs/per-diem-locations'),
  updateJob: (jobNo: string, payload: JobMaintenanceUpdate) =>
    request<JobDetail>(`/jobs/${encodeURIComponent(jobNo)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  jobFields: (jobNo: string) =>
    request<JobFieldSet>(`/jobs/${encodeURIComponent(jobNo)}/fields`),
  updateJobFields: (jobNo: string, patch: Record<string, string>) =>
    request<JobFieldSet>(`/jobs/${encodeURIComponent(jobNo)}/fields`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  jobWrites: (jobNo: string) =>
    request<JobWriteAudit[]>(`/jobs/${encodeURIComponent(jobNo)}/writes`),
  jobPerDiem: (jobNo: string) =>
    request<JobPerDiem>(`/per-diem/job/${encodeURIComponent(jobNo)}`),
  recordPerDiemDays: (payload: PerDiemDaysInput) =>
    request<PerDiemDaysAccepted>('/per-diem/days', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  overtimeRules: () => request<OvertimeRuleRow[]>('/overtime-rules'),
  unruledStates: () => request<string[]>('/overtime-rules/unruled-states'),
  updateOvertimeRule: (id: number, payload: OvertimeRuleUpsert) =>
    request<OvertimeRuleRow[]>(`/overtime-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  addOvertimeRule: (payload: OvertimeRuleUpsert) =>
    request<OvertimeRuleRow[]>('/overtime-rules', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteOvertimeRule: (id: number) =>
    request<OvertimeRuleRow[]>(`/overtime-rules/${id}`, { method: 'DELETE' }),

  /** Job numbers assigned to the signed-in user. Orders a picker; never gates one. */
  /** Record somebody working who is not in Sage yet. Returns them pickable. */
  addProvisionalPerson: (payload: ProvisionalPersonInput) =>
    request<CrewMember>('/reference/provisional', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** The office queue: placeholders waiting to be matched to a real record. */
  provisionalPeople: (includeLinked = false) =>
    request<ProvisionalPerson[]>(
      `/admin/provisional${includeLinked ? '?include_linked=true' : ''}`,
    ),
  /** Say who a placeholder actually is; moves their hours onto the real person. */
  linkProvisional: (employeeId: number, sageEmployeeKey: string) =>
    request<ProvisionalPerson[]>(`/admin/provisional/${employeeId}/link`, {
      method: 'POST',
      body: JSON.stringify({ sage_employee_key: sageEmployeeKey }),
    }),
  /** Active technicians on nobody's crew - pickable by any supervisor. */
  floaters: () => request<CrewMember[]>('/reference/floaters'),
  /** Where jobs appear to be, from the readings. Unpinned first. */
  locationSuggestions: (unpinnedOnly = true) =>
    request<JobLocationSuggestion[]>(
      `/presence/location-suggestions?unpinned_only=${unpinnedOnly}`,
    ),
  /** Jobs that already have a pinned site. */
  jobLocations: () => request<JobLocationRow[]>('/jobs/locations'),
  /** Pin where a job is. Every future reading is classified against this. */
  setJobLocation: (jobNo: string, payload: JobLocationInput) =>
    request<JobLocationRow>(`/jobs/${encodeURIComponent(jobNo)}/location`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  /** Remove a job's pin altogether. Its readings go back to the queue. */
  clearJobLocation: (jobNo: string) =>
    request<void>(`/jobs/${encodeURIComponent(jobNo)}/location`, { method: 'DELETE' }),
  /** Readings, with distance and verdict worked out at read time. */
  checkIns: (q: { jobNo?: string; dateFrom?: string; dateTo?: string } = {}) => {
    const p = new URLSearchParams()
    if (q.jobNo) p.set('job_no', q.jobNo)
    if (q.dateFrom) p.set('date_from', q.dateFrom)
    if (q.dateTo) p.set('date_to', q.dateTo)
    const qs = p.toString()
    return request<SiteCheckIn[]>(`/presence/check-ins${qs ? `?${qs}` : ''}`)
  },
  /** Record that somebody was standing on the job site. */
  checkIn: (payload: SiteCheckInInput) =>
    request<SiteCheckIn>('/presence/check-in', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** The hour lines behind one cell of the crew-week grid. */
  dayLines: (employeeKey: string, dateWorked: string) =>
    request<DayLine[]>(
      `/submissions/day-lines?employee_key=${encodeURIComponent(employeeKey)}` +
        `&date=${dateWorked}`,
    ),
  /** Who already has hours on a date. A warning at entry, never a refusal. */
  dayUsage: (dateWorked: string, keys: string[]) =>
    request<DayUsage[]>(
      `/submissions/day-usage?date=${dateWorked}` +
        keys.map((k) => `&keys=${encodeURIComponent(k)}`).join(''),
    ),
  myJobs: () => request<string[]>('/jobs/mine'),
  unassignedJobs: () => request<UnassignedJob[]>('/jobs/unassigned'),
  assignmentCandidates: () =>
    request<JobAssignmentCandidate[]>('/jobs/assignment-candidates'),
  jobAssignments: (jobNo: string) =>
    request<JobAssignee[]>(`/jobs/${encodeURIComponent(jobNo)}/assignments`),
  setJobAssignments: (jobNo: string, employeeIds: number[]) =>
    request<JobAssignee[]>(`/jobs/${encodeURIComponent(jobNo)}/assignments`, {
      method: 'PUT',
      body: JSON.stringify({
        employee_ids: employeeIds,
        assignment_role: 'ProjectManager',
      }),
    }),

  jobClassifications: (jobNo: string, dateWorked: string, employeeKey?: string) =>
    request<JobClassificationOption[]>(
      `/reference/jobs/${encodeURIComponent(jobNo)}/classifications` +
        `?date_worked=${dateWorked}` +
        (employeeKey ? `&sage_employee_key=${encodeURIComponent(employeeKey)}` : ''),
    ),

  perDiemRegister: (q: PerDiemRegisterQuery = {}) => {
    const p = new URLSearchParams()
    if (q.month) p.set('month', q.month)
    else if (q.weekEnding) p.set('week_ending', q.weekEnding)
    if (q.groupBy) p.set('group_by', q.groupBy)
    if (q.crew != null) p.set('crew', String(q.crew))
    if (q.jobNo) p.set('job_no', q.jobNo)
    const qs = p.toString()
    return request<PerDiemRegister>(`/per-diem/register${qs ? `?${qs}` : ''}`)
  },
  removePerDiemDays: (timeEntryIds: string[], reason?: string) =>
    request<PerDiemRemoved>('/per-diem/remove', {
      method: 'POST',
      body: JSON.stringify({ time_entry_ids: timeEntryIds, reason: reason ?? null }),
    }),

  perDiemRates: () => request<PerDiemRateRow[]>('/per-diem/rates'),
  updatePerDiemRate: (id: number, payload: PerDiemRateUpsert) =>
    request<PerDiemRateRow>(`/per-diem/rates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  addPerDiemLocation: (payload: PerDiemRateUpsert) =>
    request<PerDiemRateRow[]>('/per-diem/rates', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  payPeriods: () => request<PayPeriod[]>('/submissions/pay-periods'),
  crewWeek: (weekEnding: string) =>
    request<CrewWeek[]>(`/submissions/crew-week?week_ending=${weekEnding}`),
  submissionAudit: (submissionId: string) =>
    request<AuditEntry[]>(`/submissions/${submissionId}/audit`),
  weekChanges: (weekEnding: string) =>
    request<ChangeRow[]>(`/submissions/changes?week_ending=${weekEnding}`),

  // --- Sage menu: Send to Sage and Bridge status ---
  sageBridge: () => request<SageBridgeCompany[]>('/sage-export/bridge'),
  setSageWriting: (company: SageCompany, writingOn: boolean) =>
    request<SageBridgeCompany[]>(`/sage-export/bridge/${company}`, {
      method: 'PUT',
      body: JSON.stringify({ writing_on: writingOn }),
    }),
  exportPreflight: (company: SageCompany, weekEnding: string) =>
    request<ExportPreflight>(`/sage-export/preflight?company=${company}&week_ending=${weekEnding}`),
  exportBatches: (company: SageCompany, weekEnding?: string) =>
    request<ExportBatch[]>(
      `/sage-export/batches?company=${company}${weekEnding ? `&week_ending=${weekEnding}` : ''}`,
    ),
  sendToSage: (company: SageCompany, weekEnding: string, timeEntryIds: string[] | null, note: string | null) =>
    request<ExportBatch>('/sage-export/batches', {
      method: 'POST',
      body: JSON.stringify({ company, week_ending: weekEnding, time_entry_ids: timeEntryIds, note }),
    }),
  cancelExportBatch: (batchId: number) =>
    request<ExportBatch>(`/sage-export/batches/${batchId}/cancel`, { method: 'POST' }),
  cancelExportLine: (lineId: number) =>
    request<ExportBatch>(`/sage-export/lines/${lineId}/cancel`, { method: 'POST' }),
  retryExportLine: (lineId: number) =>
    request<ExportBatch>(`/sage-export/lines/${lineId}/retry`, { method: 'POST' }),
  forgetTstSends: (weekEnding: string) =>
    request<{ forgotten: number }>(`/sage-export/tst/forget?week_ending=${weekEnding}`, { method: 'POST' }),

  // --- Supervisor cutoff (Pay setup) ---
  cutoff: () => request<CutoffOverview>('/cutoff'),
  setCutoff: (payload: Pick<CutoffSetting, 'is_enabled' | 'days_after_week_end' | 'cutoff_time'>) =>
    request<CutoffOverview>('/cutoff', { method: 'PUT', body: JSON.stringify(payload) }),
  reopenWeek: (weekEnding: string, openUntilUtc: string, note: string | null) =>
    request<CutoffOverview>(`/cutoff/weeks/${weekEnding}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ open_until_utc: openUntilUtc, note }),
    }),
  closeWeek: (weekEnding: string) =>
    request<CutoffOverview>(`/cutoff/weeks/${weekEnding}/close`, { method: 'POST' }),
  register: (filters: RegisterFilters = {}) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== '' && v !== false) q.set(k, String(v))
    }
    const qs = q.toString()
    return request<RegisterResponse>(`/register${qs ? `?${qs}` : ''}`)
  },
  weeks: (mine = false) =>
    request<WeekSummary[]>(`/submissions/weeks?mine=${mine}`),
  week: (jobNo: string, weekStart: string) =>
    request<WeekDetail>(
      `/submissions/week?job_no=${encodeURIComponent(jobNo)}&week_start=${weekStart}`,
    ),

  // --- Administration ---
  adminEmployees: (department?: string) =>
    request<EmployeeAdminRow[]>(
      `/admin/employees${department ? `?department=${encodeURIComponent(department)}` : ''}`,
    ),
  adminUpsertEmployee: (sageEmployeeKey: string, payload: EmployeeUpsert) =>
    request<EmployeeAdminRow>(`/admin/employees/${encodeURIComponent(sageEmployeeKey)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  adminSetClassification: (sageEmployeeKeys: string[], wageClassificationId: number) =>
    request<void>('/admin/employees/classifications', {
      method: 'PUT',
      body: JSON.stringify({
        sage_employee_keys: sageEmployeeKeys,
        wage_classification_id: wageClassificationId,
      }),
    }),
  adminSetPin: (sageEmployeeKey: string, pin: string) =>
    request<void>(`/admin/employees/${encodeURIComponent(sageEmployeeKey)}/pin`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),
  // --- Prevailing wage rates ---
  wageClassifications: () =>
    request<WageClassificationRow[]>('/wages/classifications'),
  wageJobs: (missingOnly = false) =>
    request<JobWageSummary[]>(`/wages/jobs?missing_only=${missingOnly}`),
  wageJob: (jobNo: string) =>
    request<JobWageDetail>(`/wages/jobs/${encodeURIComponent(jobNo)}`),
  addWageRate: (jobNo: string, payload: JobWageRateUpsert) =>
    request<JobWageRateRow>(`/wages/jobs/${encodeURIComponent(jobNo)}/rates`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateWageRate: (rateId: number, payload: JobWageRateUpsert) =>
    request<JobWageRateRow>(`/wages/rates/${rateId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  deleteWageRate: (rateId: number) =>
    request<void>(`/wages/rates/${rateId}`, { method: 'DELETE' }),

  adminWorkTypes: () => request<WorkTypeAdminRow[]>('/admin/work-types'),
  adminCreateWorkType: (payload: WorkTypeUpsert) =>
    request<WorkTypeAdminRow>('/admin/work-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  adminUpdateWorkType: (id: number, payload: WorkTypeUpsert) =>
    request<WorkTypeAdminRow>(`/admin/work-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  adminDeleteWorkType: (id: number) =>
    request<void>(`/admin/work-types/${id}`, { method: 'DELETE' }),
  adminCostCodes: () => request<CostCodeMaster[]>('/admin/cost-codes'),
  adminEarningsCodes: () =>
    request<EarningsCodeAdminRow[]>('/admin/earnings-codes'),
  adminUpdateEarningsCode: (code: string, payload: EarningsCodeUpdate) =>
    request<EarningsCodeAdminRow>(`/admin/earnings-codes/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  adminCrews: () => request<CrewDetail[]>('/admin/crews'),
  adminSetCrewMembers: (supervisorEmployeeId: number, employeeIds: number[]) =>
    request<CrewDetail>(`/admin/crews/${supervisorEmployeeId}/members`, {
      method: 'PUT',
      body: JSON.stringify({ employee_ids: employeeIds }),
    }),

  // --- Certifications -------------------------------------------------------
  // Every /certs endpoint 404s outside the preview gate; certAccess is the one
  // that answers everyone, so the menu can ask without an error.
  certAccess: () => request<CertAccess>('/certs/access'),
  /** Only ever the signed-in person: there is no id to pass. */
  myCerts: () => request<MyCerts>('/certs/me'),
  crewCerts: (includeFloaters = false) =>
    request<CrewCerts>(`/certs/crew?include_floaters=${includeFloaters}`),
  certMatrix: (q: CertMatrixQuery = {}) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) {
      if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
    }
    const qs = p.toString()
    return request<CertMatrix>(`/certs/matrix${qs ? `?${qs}` : ''}`)
  },
  employeeCerts: (employeeId: number, asOf?: string) =>
    request<EmployeeCerts>(
      `/certs/employees/${employeeId}${asOf ? `?as_of=${encodeURIComponent(asOf)}` : ''}`,
    ),
  addCert: (employeeId: number, payload: EmployeeCertInput) =>
    request<EmployeeCerts>(`/certs/employees/${employeeId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  renewCert: (certId: number, payload: EmployeeCertInput) =>
    request<EmployeeCerts>(`/certs/${certId}/renew`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  /** Only the keys present are changed -- send null to clear a field. */
  correctCert: (certId: number, payload: EmployeeCertInput) =>
    request<EmployeeCerts>(`/certs/${certId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  verifyCert: (certId: number) =>
    request<EmployeeCerts>(`/certs/${certId}/verify`, { method: 'POST' }),
  voidCert: (certId: number, reason: string) =>
    request<EmployeeCerts>(`/certs/${certId}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  reevaluateCert: (certId: number, required: boolean, reason: string) =>
    request<EmployeeCerts>(`/certs/${certId}/reevaluate`, {
      method: 'POST',
      body: JSON.stringify({ required, reason }),
    }),
  certCategories: () => request<CertCategoryRow[]>('/certs/setup/categories'),
  createCertCategory: (payload: CertCategoryInput) =>
    request<CertCategoryRow[]>('/certs/setup/categories', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCertCategory: (id: number, payload: CertCategoryInput) =>
    request<CertCategoryRow[]>(`/certs/setup/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  certTypes: () => request<CertTypeRow[]>('/certs/setup/types'),
  createCertType: (payload: CertTypeInput) =>
    request<CertTypeRow[]>('/certs/setup/types', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCertType: (id: number, payload: CertTypeInput) =>
    request<CertTypeRow[]>(`/certs/setup/types/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  certRelations: (includeClosed = false) =>
    request<CertRelationRow[]>(`/certs/setup/relations?include_closed=${includeClosed}`),
  createCertRelation: (payload: CertRelationInput) =>
    request<CertRelationRow[]>('/certs/setup/relations', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  closeCertRelation: (id: number) =>
    request<CertRelationRow[]>(`/certs/setup/relations/${id}/close`, { method: 'POST' }),
  certRequirements: (includeClosed = false) =>
    request<CertRequirementRow[]>(`/certs/setup/requirements?include_closed=${includeClosed}`),
  createCertRequirement: (payload: CertRequirementInput) =>
    request<CertRequirementRow[]>('/certs/setup/requirements', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  closeCertRequirement: (id: number) =>
    request<CertRequirementRow[]>(`/certs/setup/requirements/${id}/close`, { method: 'POST' }),

  // Alert email. Send and the settings are Admin only; the API re-checks.
  certNotify: () => request<CertNotifyOverview>('/certs/notify'),
  updateCertNotifySettings: (payload: CertNotifySettings) =>
    request<CertNotifyOverview>('/certs/notify/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  addCertNotifyRecipient: (payload: { email: string; display_name: string }) =>
    request<CertNotifyOverview>('/certs/notify/recipients', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateCertNotifyRecipient: (
    id: number,
    payload: { email?: string; display_name?: string; is_active?: boolean },
  ) =>
    request<CertNotifyOverview>(`/certs/notify/recipients/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  sendCertAlerts: () =>
    request<CertNotifySendResponse>('/certs/notify/send', { method: 'POST' }),
  sendCertTestEmail: (toEmail: string) =>
    request<CertNotifySendResponse>('/certs/notify/test', {
      method: 'POST',
      body: JSON.stringify({ to_email: toEmail }),
    }),
  /** Crew-day warning. 404 outside the certifications preview: callers
   *  treat any failure as "nothing to say". */
  certDayCheck: (jobNo: string, dateWorked: string, keys: string[]) =>
    request<CertDayCheck>(
      `/certs/day-check?job_no=${encodeURIComponent(jobNo)}&date=${dateWorked}` +
        keys.map((k) => `&keys=${encodeURIComponent(k)}`).join(''),
    ),
  setCertContact: (employeeId: number, payload: { alert_email: string | null; opt_out: boolean }) =>
    request<CertNotifyContact>(`/certs/employees/${employeeId}/contact`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  /* Feedback from the help panel. Anyone signed in sends and sees their
   * own; the inbox and replies are Admin only. */
  sendFeedback: (payload: { category: FeedbackCategory; message: string; page: string | null }) =>
    request<FeedbackItem>('/feedback', { method: 'POST', body: JSON.stringify(payload) }),
  myFeedback: () => request<FeedbackItem[]>('/feedback/mine'),
  allFeedback: (status?: FeedbackStatus) =>
    request<FeedbackItem[]>(`/feedback${status ? `?status=${status}` : ''}`),
  updateFeedback: (id: number, payload: { status?: FeedbackStatus; admin_note?: string | null }) =>
    request<FeedbackItem>(`/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
}
